/**
 * Centralized DreamCast API HTTP client (pattern: support-wwg-store-web
 * shared/api/client.js).
 *
 * All DreamCast namespace modules (media, playlists, speakers, ...) route
 * their calls through `request()` here so that:
 *   - The MSAL bearer token (scope `api.access`) is attached in one place.
 *     The API's AuthMiddleware validates it and derives the acting user from
 *     the token's `preferred_username` — no more hardcoded support-user header.
 *   - The base URL is configured in one place (`dreamcastApiBase` in
 *     regionConfig, per domain).
 *   - Error/response parsing is uniform.
 *
 * TOKEN PROVIDER
 * --------------
 * `request()` is a plain module function, but the MSAL token lives in the
 * `useAuth` React hook. `App` registers a token provider once auth resolves
 * via `setAuthTokenProvider(getToken)`; the client then acquires a bearer
 * token on demand without being a hook itself.
 */

import { ENVIRONMENT_CONFIG } from "shared/globals";

let _tokenProvider = null;

// A request fired on first mount can race auth: a component may call the API
// before `App` has registered the token provider. We expose a promise that
// resolves the moment the provider is registered so `request()` can wait for
// it (bounded by a timeout so a genuinely-unauthenticated context — e.g. the
// local API with AUTH_BYPASS_ENABLED — still proceeds rather than hanging).
let _resolveTokenProviderReady;
let _tokenProviderReady = new Promise((resolve) => {
  _resolveTokenProviderReady = resolve;
});

/**
 * Register the function used to obtain an MSAL bearer token. Wired once from
 * `App` with the `getToken` returned by the Auth container.
 *
 * @param {() => Promise<string>} fn Resolves to a raw access token.
 */
export function setAuthTokenProvider(fn) {
  _tokenProvider = fn;
  if (fn && _resolveTokenProviderReady) {
    _resolveTokenProviderReady();
    _resolveTokenProviderReady = null;
  }
}

export function buildUrl(path) {
  const base = ENVIRONMENT_CONFIG.dreamcastApiBase;
  const normalizedBase = base.endsWith("/") ? base.slice(0, -1) : base;
  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  return `${normalizedBase}${normalizedPath}`;
}

async function buildHeaders(headers = {}) {
  const finalHeaders = {
    "Content-Type": "application/json",
    ...headers,
  };

  if (!_tokenProvider) {
    await Promise.race([
      _tokenProviderReady,
      new Promise((resolve) => setTimeout(resolve, 5000)),
    ]);
  }

  if (_tokenProvider) {
    try {
      const token = await _tokenProvider();
      if (token) {
        finalHeaders.Authorization = `Bearer ${token}`;
      }
    } catch (error) {
      // Leave the request unauthenticated; the API responds 401 and the
      // caller's error path surfaces it. Locally AUTH_BYPASS_ENABLED covers it.
      // eslint-disable-next-line no-console
      console.error("DreamCast api client: token acquisition failed", error);
    }
  }

  return finalHeaders;
}

export async function parseResponse(response) {
  const contentType = response.headers.get("content-type") || "";
  const isJson = contentType.toLowerCase().includes("json");

  const data = isJson ? await response.json() : await response.text();

  if (!response.ok) {
    const error = new Error(
      data?.error?.message ||
        data?.message ||
        `Request failed (${response.status})`,
    );
    error.status = response.status;
    error.data = data;
    throw error;
  }

  return data;
}

/**
 * Perform an authenticated JSON request against the DreamCast API.
 *
 * Keeps the namespace modules' existing convention: `options` is passed to
 * fetch (so `body` is already stringified, `signal` passes through) with the
 * auth/JSON headers merged in.
 * 
 * Default timeout: 30 seconds. Can be overridden via options.timeout (in ms).
 */
export async function request(path, options = {}) {
  const timeout = options.timeout || 30000; // 30 seconds default
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);
  
  // If caller provided a signal, abort that too when timeout fires
  const signal = options.signal;
  if (signal) {
    if (signal.aborted) {
      clearTimeout(timeoutId);
      throw new Error('Request aborted');
    }
    signal.addEventListener('abort', () => controller.abort());
  }
  
  try {
    const response = await fetch(buildUrl(path), {
      ...options,
      signal: controller.signal,
      headers: await buildHeaders(options.headers),
    });

    return parseResponse(response);
  } catch (error) {
    if (error.name === 'AbortError') {
      throw new Error(`Request timeout after ${timeout}ms`);
    }
    throw error;
  } finally {
    clearTimeout(timeoutId);
  }
}

/**
 * Like `request()`, but reports real upload progress via `onProgress(loaded,
 * total)`. Uses XMLHttpRequest because fetch has no upload-progress event —
 * needed for large chunked media uploads where users otherwise stare at a
 * progress bar that jumps to ~95% and stalls.
 *
 * Timeout is an INACTIVITY timeout (default 120s): it resets every time bytes
 * move, so a slow-but-progressing upload of any size never gets aborted —
 * only a genuinely stalled connection does.
 */
export async function requestWithProgress(path, options = {}, onProgress) {
  const timeout = options.timeout || 120000; // inactivity window
  const headers = await buildHeaders(options.headers);

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open(options.method || "GET", buildUrl(path));

    Object.entries(headers).forEach(([key, value]) => {
      xhr.setRequestHeader(key, value);
    });

    if (options.signal) {
      if (options.signal.aborted) {
        xhr.abort();
      }
      options.signal.addEventListener("abort", () => xhr.abort());
    }

    // Inactivity timeout — restarted whenever upload bytes move.
    let timeoutId;
    const armTimeout = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        xhr.abort();
        reject(new Error(`Upload stalled — no progress for ${timeout}ms`));
      }, timeout);
    };
    armTimeout();

    xhr.upload.onprogress = (event) => {
      armTimeout();
      if (onProgress && event.lengthComputable) {
        onProgress(event.loaded, event.total);
      }
    };
    // The server-side phase after the last byte (temp write, metadata
    // extraction, CDN push) produces no progress events — give it its own
    // window rather than the mid-upload one.
    xhr.upload.onload = () => armTimeout();

    xhr.onload = () => {
      clearTimeout(timeoutId);
      const contentType = xhr.getResponseHeader("content-type") || "";
      const isJson = contentType.toLowerCase().includes("json");
      let data;
      try {
        data = isJson ? JSON.parse(xhr.responseText || "{}") : xhr.responseText;
      } catch {
        data = xhr.responseText;
      }

      if (xhr.status < 200 || xhr.status >= 300) {
        const error = new Error(
          data?.error?.message ||
            data?.message ||
            `Request failed (${xhr.status})`,
        );
        error.status = xhr.status;
        error.data = data;
        reject(error);
        return;
      }

      resolve(data);
    };

    xhr.onerror = () => {
      clearTimeout(timeoutId);
      reject(new Error("Network error"));
    };
    xhr.onabort = () => {
      clearTimeout(timeoutId);
      const error = new Error("Upload aborted");
      error.name = "AbortError";
      reject(error);
    };

    xhr.send(options.body);
  });
}
